import { z } from 'zod';
import { categoryKeySchema, roleSchema, userStatusSchema } from './enums';
/**
 * The three directory fields we hold, and nothing more.
 *
 * `entraObjectId` is the foreign key — never email, never userPrincipalName,
 * because both change when someone marries or a domain is tidied up.
 *
 * `displayName` and `department` are refreshed on the user row by the daily
 * Graph reconciliation job. Copies denormalised onto topics and comments stay
 * frozen, because those are a historical record rather than a directory mirror.
 *
 * Job title, photo, manager, phone, email and office are deliberately absent.
 * They are fetched from Microsoft Graph on demand and never stored.
 */
export const forumUserSchema = z.object({
    id: z.string(),
    entraObjectId: z.string(),
    displayName: z.string(),
    department: z.string().nullable(),
    status: userStatusSchema,
    suspendedUntil: z.coerce.date().nullable(),
    suspendedReason: z.string().nullable(),
    createdAt: z.coerce.date(),
    lastSeenAt: z.coerce.date().nullable(),
});
/**
 * The signed-in principal, as returned by GET /api/me.
 *
 * Roles are resolved from the database on each request (with a short in-memory
 * cache) rather than stamped into the session cookie. If roles lived in the
 * cookie, a suspension would not take effect until the session refreshed —
 * exactly the wrong behaviour for a moderation action.
 */
export const principalSchema = z.object({
    userId: z.string(),
    entraObjectId: z.string(),
    displayName: z.string(),
    department: z.string().nullable(),
    status: userStatusSchema,
    roles: z.array(roleSchema),
    /** Categories this user owns. Held in the database, not Entra — it changes too often. */
    ownedCategories: z.array(categoryKeySchema),
    /** True when the role comes from the break-glass Entra group rather than the database. */
    isBreakGlassAdmin: z.boolean(),
    /** False while suspended. Read access continues; writes do not. */
    canParticipate: z.boolean(),
});
/** A single result from the Entra directory people picker. Never persisted. */
export const directorySearchResultSchema = z.object({
    entraObjectId: z.string(),
    displayName: z.string(),
    mail: z.string().nullable(),
    department: z.string().nullable(),
    jobTitle: z.string().nullable(),
    /** True when this person already has a forum membership row. */
    isExistingMember: z.boolean(),
});
export const directorySearchQuerySchema = z.object({
    q: z.string().min(3, 'Enter at least three characters').max(64),
});
//# sourceMappingURL=user.js.map